import {
  Box,
  FormControlLabel,
  Paper,
  Switch,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useEffect, useState } from 'react';
import ApiService from '../../../functions/apiService';
import { useSearchParams } from 'react-router-dom';
import moment from 'moment';

const formatDate = (time) => moment(time, 'HH:mm:ss').format('hA');

const to12Hour = (time24) => {
  if (!time24) return { hour: '', minute: '00', period: 'AM' };
  const [h, m] = time24.split(':').map(Number);
  return {
    hour: String(h % 12 || 12),
    minute: String(m).padStart(2, '0'),
    period: h >= 12 ? 'PM' : 'AM',
  };
};

const to24Hour = (hour, minute, period) => {
  if (!hour) return '';
  let h = parseInt(hour, 10);
  if (period === 'PM' && h !== 12) h += 12;
  if (period === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

const TIME_HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1));
const TIME_MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));

const buildRotation = (days) =>
  days.map((item) => {
    if (item.value.includes('CUSTOM')) {
      return item.customStart && item.customEnd
        ? `Y(${item.customStart}-${item.customEnd})`
        : 'Y';
    }
    return item.value.join('');
  }).join(',');

export const RotationForm = ({
  register,
  errors,
  setValue,
  watch,
  user
}) => {
  const [params] = useSearchParams();
  const empId = params.get('empId');
  const isRotationActive = watch('Unique_Rotation');
  const [effectiveDate, setEffectiveDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [days, setDays] = useState([{ value: [], customStart: '', customEnd: '' }]);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [isShiftTypesLoading, setIsShiftTypesLoading] = useState(false);

  const toggleRotation = (e) => setValue('Unique_Rotation', e.target.checked);

  const onDateChange = (e) => {
    setEffectiveDate(e.target.value);
    setValue('Rot_Effective_Date', e.target.value);
  };

  const handleDayChange = (index, newValue) => {
    setDays((prev) => {
      const prevDay = prev[index];
      let finalValue = newValue;

      // Custom is mutually exclusive — selecting it clears others
      if (newValue.includes('CUSTOM') && !prevDay.value.includes('CUSTOM')) {
        finalValue = ['CUSTOM'];
      }
      // Selecting anything else while Custom is active removes Custom
      else if (prevDay.value.includes('CUSTOM') && newValue.length > 1) {
        finalValue = newValue.filter((v) => v !== 'CUSTOM');
      }

      const updatedDays = prev.map((day, i) =>
        i === index ? { ...day, value: finalValue } : day
      );
      setValue('Rotation', buildRotation(updatedDays), { shouldDirty: true });
      return updatedDays;
    });
  };

  const handleCustomTimeChange = (index, field, value) => {
    setDays((prev) => {
      const updatedDays = prev.map((day, i) =>
        i === index ? { ...day, [field]: value } : day
      );
      setValue('Rotation', buildRotation(updatedDays), { shouldDirty: true });
      return updatedDays;
    });
  };

  const handleAddDay = () => {
    setDays((prev) => [...prev, { value: [], customStart: '', customEnd: '' }]);
  };

  const handleDeleteDay = (index) => {
    const newDays = days.filter((_, i) => i !== index);
    setDays(newDays);
    setValue('Rotation', buildRotation(newDays));
  };

  useEffect(() => {
    setValue('Rot_Effective_Date', effectiveDate);

    const getShiftTypes = async () => {
      try {
        setIsShiftTypesLoading(true);
        const { data } = await ApiService.get('/shiftTypes', {
          date: effectiveDate,
        });
        setShiftTypes(
          (data?.data || [])
            .filter(item => item.Shift !== '24')
            .map(item => ({
              shiftCode: item.ShiftCode,
              shiftHours: item.Shift_Hours,
              shiftName: item.Shift,
              shiftStart: item.Start_Time,
              shiftEnd: item.End_Time,
            }))
        );
        setIsShiftTypesLoading(false);
      } catch (error) {
        console.error(error);
      }
    };

    getShiftTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    const currentEmployee = user.employee_rotation;

    if (!currentEmployee) return;

    setValue('Rot_Effective_Date', currentEmployee.Effective_Date || effectiveDate);
    setValue('Unique_Rotation', true);

    setDays(
      currentEmployee.Rotation.split(',').map((item) => {
        const yMatch = item.match(/^Y\(([^-]+)-([^)]+)\)/);
        if (yMatch) {
          return { value: ['CUSTOM'], customStart: yMatch[1], customEnd: yMatch[2] };
        }
        return { value: item.split(''), customStart: '', customEnd: '' };
      })
    );
  }, [user]);

  return (
    <Paper className="p-4 my-3">
      <p className="fw-500 fs-5">Rotation</p>
      {isShiftTypesLoading ? (
        <Box className="w-100 h-100 d-flex justify-content-center align-items-center">
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControlLabel
            control={
              <Switch
                onChange={toggleRotation}
                checked={isRotationActive}
                {...register('Unique_Rotation')}
              />
            }
            label="Activate Rotation"
          />

          <Box>
            <TextField
              color="secondary"
              type="date"
              variant="outlined"
              label="Effective Date"
              disabled={!isRotationActive}
              {...register('Rot_Effective_Date', {
                required: 'Effective Date is required',
                onChange: onDateChange,
              })}
            />
            {errors.Rot_Effective_Date && (
              <p style={{ color: 'red' }}>
                {errors.Rot_Effective_Date?.message}
              </p>
            )}
          </Box>

          {days.map((day, index) => (
            <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>{`Day ${index + 1}`}</InputLabel>
                  <Select
                    label={`Day ${index + 1}`}
                    value={day.value}
                    onChange={(e) => handleDayChange(index, e.target.value)}
                    disabled={!isRotationActive}
                    multiple
                  >
                    {shiftTypes.map((shift) => (
                      <MenuItem key={shift.shiftCode} value={String(shift.shiftCode)}>
                        {`(${formatDate(shift.shiftStart)} - ${formatDate(shift.shiftEnd)})`}
                      </MenuItem>
                    ))}
                    <MenuItem value="X">Not Working</MenuItem>
                    <MenuItem value="CUSTOM">Custom</MenuItem>
                  </Select>
                </FormControl>
                <IconButton
                  onClick={() => handleDeleteDay(index)}
                  disabled={!isRotationActive || days.length === 1}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>

              {day.value.includes('CUSTOM') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1, ml: 1, pb: 1, borderBottom: '1px solid #e0e0e0', flexWrap: 'wrap' }}>
                  {[
                    { field: 'customStart', label: 'Start' },
                    { field: 'customEnd', label: 'End' },
                  ].map(({ field, label }) => {
                    const { hour, minute, period } = to12Hour(day[field]);
                    const update = (part, val) => {
                      const h = part === 'hour' ? val : hour;
                      const m = part === 'minute' ? val : minute;
                      const p = part === 'period' ? val : period;
                      handleCustomTimeChange(index, field, to24Hour(h || '12', m || '00', p));
                    };
                    return (
                      <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box sx={{ fontSize: '0.75rem', color: 'rgba(0,0,0,0.6)', whiteSpace: 'nowrap', mr: 0.5 }}>{label}</Box>
                        <Select size="small" value={hour} onChange={(e) => update('hour', e.target.value)} disabled={!isRotationActive} displayEmpty sx={{ minWidth: 52 }}>
                          <MenuItem value="" disabled>HH</MenuItem>
                          {TIME_HOURS.map(h => <MenuItem key={h} value={h}>{h}</MenuItem>)}
                        </Select>
                        <span style={{ fontWeight: 'bold' }}>:</span>
                        <Select size="small" value={minute} onChange={(e) => update('minute', e.target.value)} disabled={!isRotationActive} displayEmpty sx={{ minWidth: 52 }}>
                          <MenuItem value="" disabled>MM</MenuItem>
                          {TIME_MINUTES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </Select>
                        <Select size="small" value={period} onChange={(e) => update('period', e.target.value)} disabled={!isRotationActive} sx={{ minWidth: 62 }}>
                          <MenuItem value="AM">AM</MenuItem>
                          <MenuItem value="PM">PM</MenuItem>
                        </Select>
                      </Box>
                    );
                  })}
                </Box>
              )}
            </Box>
          ))}

          <Button
            variant="outlined"
            color="primary"
            onClick={handleAddDay}
            disabled={!isRotationActive}
          >
            + Add
          </Button>
        </Box>
      )}
    </Paper>
  );
};
